import React from 'react';
import { Typography } from '@mui/material';
import TelegramIcon from '@mui/icons-material/Telegram';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Reservation, ReservationRate } from '../../../types/Reservation';
import { Button, ButtonColor, ButtonSize, ButtonVariant } from '../../Button';
import { ButtonPaddingSize } from '../../Button/Button.types';
import RatingForm from '../RatingForm';
import './ReservationActions.css';

const formatDateForGoogleCalendar = (date: Date): string => {
  return date
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '');
};

const buildEventTitle = (reservation: Reservation): string => {
  const { course, teacher } = reservation;

  if (course.label && course.institution) {
    return `Clase de ${course.label} - ${course.institution.label}`;
  }

  return `Clase con ${teacher.first_name} ${teacher.last_name}`;
};

const buildGoogleCalendarUrl = (reservation: Reservation): string => {
  const text = encodeURIComponent(buildEventTitle(reservation));
  const details = encodeURIComponent(
    `Clase particular con ${reservation.teacher.first_name} ${reservation.teacher.last_name} a través de Estudi.`
  );
  const start = formatDateForGoogleCalendar(reservation.event.start);
  const end = formatDateForGoogleCalendar(reservation.event.end);

  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${start}/${end}&details=${details}`;
};

const downloadIcs = (reservation: Reservation) => {
  const fmt = (d: Date) => formatDateForGoogleCalendar(d);
  const summary = buildEventTitle(reservation);
  const description = `Clase particular con ${reservation.teacher.first_name} ${reservation.teacher.last_name} a través de Estudi.`;

  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Estudi//Estudi//ES',
    'BEGIN:VEVENT',
    `DTSTART:${fmt(reservation.event.start)}`,
    `DTEND:${fmt(reservation.event.end)}`,
    `SUMMARY:${summary}`,
    `DESCRIPTION:${description}`,
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');

  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = `clase-estudi-${reservation.external_id}.ics`;
  link.click();
  URL.revokeObjectURL(url);
};

interface ActionCardProps {
  iconClass: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  children: React.ReactNode;
}

const ActionCard: React.FC<ActionCardProps> = ({
  iconClass,
  icon,
  title,
  description,
  children
}) => (
  <div className="action-card">
    <div className={`action-card-icon ${iconClass}`}>{icon}</div>
    <div className="action-card-content">
      <Typography variant="body1" sx={{ fontWeight: 600, color: 'var(--gray-900)', mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: 'var(--gray-500)', mb: 1.5 }}>
        {description}
      </Typography>
      {children}
    </div>
  </div>
);

interface ReservationActionsProps {
  reservation: Reservation;
  isClassInFuture: boolean;
  telegramLink: string | null;
  existingRate: ReservationRate | null | undefined;
  onRatingSubmit: (stars: number, comment: string) => Promise<void>;
  telegramOnly?: boolean;
}

const ReservationActions: React.FC<ReservationActionsProps> = ({
  reservation,
  isClassInFuture,
  telegramLink,
  existingRate,
  onRatingSubmit,
  telegramOnly = false
}) => {
  if (telegramOnly) {
    return (
      <div className="reservation-info-card">
        <div className="reservation-info-card-header">
          <p className="reservation-info-card-title">Comunicarme con mi profe</p>
          <TelegramIcon sx={{ color: '#0288d1', fontSize: '1.25rem' }} />
        </div>
        <Typography variant="body2" sx={{ color: 'var(--gray-500)', mb: 1.5 }}>
          Unite al Grupo exclusivo entre vos y tu profe para comunicarse y así contarle lo que estás
          necesitando
        </Typography>
        <div className="action-card-buttons">
          {telegramLink ? (
            <Button
              variant={ButtonVariant.CONTAINED}
              color={ButtonColor.PRIMARY}
              size={ButtonSize.SM}
              paddingSize={ButtonPaddingSize.SM}
              onClick={() => window.open(telegramLink, '_blank', 'noopener,noreferrer')}>
              Unirme al Grupo
            </Button>
          ) : (
            <Typography variant="body2" sx={{ color: 'var(--gray-500)', fontStyle: 'italic' }}>
              El link de invitación te llegará por mail y WhatsApp.
            </Typography>
          )}
          <Button
            variant={ButtonVariant.OUTLINED}
            color={ButtonColor.PRIMARY}
            size={ButtonSize.SM}
            paddingSize={ButtonPaddingSize.SM}
            onClick={() => window.open('/help/telegram', '_blank', 'noopener,noreferrer')}>
            Ayuda con el Grupo
          </Button>
        </div>
        <Typography variant="body2" className="action-card-note">
          Tené en cuenta que es un Grupo de Telegram por lo cual deberás primero tener la aplicación
          descargada
        </Typography>
      </div>
    );
  }

  if (isClassInFuture) {
    return (
      <div className="reservation-info-card">
        <div className="reservation-info-card-header">
          <p className="reservation-info-card-title">Agregar al calendario</p>
          <CalendarTodayIcon sx={{ color: '#388e3c', fontSize: '1.1rem' }} />
        </div>
        <Typography variant="body2" sx={{ color: 'var(--gray-500)', mb: 1.5 }}>
          No te olvides de la clase. Guardala en tu calendario favorito.
        </Typography>
        <div className="action-card-buttons">
          <Button
            variant={ButtonVariant.CONTAINED}
            color={ButtonColor.PRIMARY}
            size={ButtonSize.SM}
            paddingSize={ButtonPaddingSize.SM}
            onClick={() =>
              window.open(buildGoogleCalendarUrl(reservation), '_blank', 'noopener,noreferrer')
            }>
            Google Calendar <OpenInNewIcon sx={{ fontSize: '0.9rem', ml: 0.5 }} />
          </Button>
          <Button
            variant={ButtonVariant.OUTLINED}
            color={ButtonColor.PRIMARY}
            size={ButtonSize.SM}
            paddingSize={ButtonPaddingSize.SM}
            onClick={() => downloadIcs(reservation)}>
            Descargar .ics
          </Button>
        </div>
      </div>
    );
  }

  return (
    <ActionCard
      iconClass="rating"
      icon={<StarBorderIcon />}
      title="Calificá al profe"
      description={
        existingRate
          ? 'Ya calificaste esta clase.'
          : 'Tu opinión ayuda a otros alumnos a elegir mejor. ¿Cómo estuvo la clase?'
      }>
      <RatingForm
        existingRate={existingRate}
        reservationId={reservation.external_id}
        student={reservation.student}
        onSubmit={onRatingSubmit}
      />
    </ActionCard>
  );
};

export default ReservationActions;
